/**
 * ThemeV2 Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore.
 * Handles ThemeV2 entity with Date↔Timestamp conversion.
 *
 * CRITICAL: Uses Client SDK signature: fromFirestore(snapshot, options)
 * NOT Admin SDK signature: fromFirestore(data)
 *
 * @module firebase-core/converters
 */

import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp
} from 'firebase/firestore';
import type { ThemeV2 } from '@modules/themes/types/themesV2.types';

/**
 * Firestore converter for ThemeV2 entity.
 *
 * Handles:
 * - toFirestore: ISO date strings → Firestore Timestamps
 * - fromFirestore: Firestore Timestamps → ISO date strings
 * - ID management: removes id in toFirestore, adds from snapshot in fromFirestore
 * - Optional fields: removes undefined values (Firestore rejects undefined)
 */
export const themeV2Converter: FirestoreDataConverter<ThemeV2> = {
  /**
   * Converts ThemeV2 to Firestore document data.
   *
   * - Removes id (Firestore stores it in document metadata)
   * - Converts createdAt/updatedAt ISO strings to Timestamps
   * - Removes undefined optional fields
   */
  toFirestore(theme: ThemeV2): any {
    const result: any = { ...theme };

    // Remove id from data (Firestore keeps it in doc metadata)
    delete result.id;

    // Convert date strings to Timestamps
    if (result.createdAt) {
      result.createdAt = Timestamp.fromDate(new Date(result.createdAt));
    }
    if (result.updatedAt) {
      result.updatedAt = Timestamp.fromDate(new Date(result.updatedAt));
    }

    // Set default order if not provided
    if (result.order === undefined || result.order === null) {
      result.order = 1;
    }

    // Remove ALL undefined fields (Firestore doesn't accept undefined)
    Object.keys(result).forEach(key => {
      if (result[key] === undefined) {
        delete result[key];
      }
    });

    return result;
  },

  /**
   * Converts Firestore document to ThemeV2.
   *
   * CRITICAL: Client SDK signature - must call snapshot.data(options)
   *
   * - Adds id from snapshot.id
   * - Converts Timestamps to ISO date strings
   */
  fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): ThemeV2 {
    // CRITICAL: Extract data from snapshot using Client SDK method
    const data = snapshot.data(options);
    const result: any = { ...data, id: snapshot.id };

    // Convert Timestamps to ISO date strings
    if (result.createdAt?.toDate) {
      result.createdAt = result.createdAt.toDate().toISOString();
    }
    if (result.updatedAt?.toDate) {
      result.updatedAt = result.updatedAt.toDate().toISOString();
    }

    return result as ThemeV2;
  }
};
